// /app/frontend/src/pages/seo/BlackSisterWifeDatingPage.jsx
// Identity-specific landing — Black sister-wife dating community.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  mobileAppSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function BlackSisterWifeDatingPage() {
  const path = '/black-sister-wife-dating';
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Black Sister Wife Dating Community', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Black Sister Wife Dating Community"
        description="Connect with Black singles, couples, and families interested in intentional sister-wife relationships and family-building."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Community"
        headline="Black Sister Wife Dating & Family Building"
        sub="A space for Black singles, couples, and families pursuing intentional plural relationships — rooted in culture, connection, and family."
        body="UnityLove centers the Black experience without flattening it. Bring your full self — your faith, your boundaries, your vision for family — and find people who understand the whole picture."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'How To Find A Sister Wife', to: '/how-to-find-a-sister-wife' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Why this community exists */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            A Community Built For Us
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              For Black singles and couples exploring sister-wife and plural family structures, the search has often meant choosing between two imperfect spaces — mainstream apps that don’t understand the relationship model, or poly communities where Black voices and experiences sit at the margins. UnityLove was built so neither has to be the trade-off.
            </p>
            <p>
              Here, Black poly and plural family-building isn’t an exception or a niche category. It’s woven into the experience of the platform itself. From the profile fields to the community spaces to the way matches are surfaced, the assumption is that you are showing up fully — culturally, spiritually, romantically, intentionally.
            </p>
          </div>
        </section>

        {/* What centering Black means */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            What Centering The Black Experience Looks Like
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="✊🏾"
              accent="amber"
              title="Cultural Fluency First"
              body="Conversations don't start at explanation. Bring your family of origin, your traditions, your faith context — they're welcomed, not interrogated."
            />
            <FeatureCard
              icon="🛡️"
              accent="rose"
              title="Reduced Fetishization"
              body="Verification, moderation, and community standards exist to protect against the racialized fetishization Black women face on mainstream apps."
            />
            <FeatureCard
              icon="📿"
              accent="violet"
              title="Faith-Aware, Not Required"
              body="Whether your plural family is rooted in spiritual practice or secular ENM ethics, you'll find people who share your framing."
            />
            <FeatureCard
              icon="👨🏾‍👩🏾‍👧🏾‍👦🏾"
              accent="emerald"
              title="Family-Building Vision"
              body="Long-term goals — children, household, generational vision — are part of the conversation from the start, not an awkward third-date reveal."
            />
          </div>
        </section>

        {/* Realities section */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            The Realities We Take Seriously
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              Plural family-building inside the Black community sits at the crossroads of a few hard truths. There’s the historical weight of how Black families have been policed and judged from the outside. There’s the ongoing reality of how Black women in particular get treated in dating spaces. There’s the way faith communities can be both a foundation and a source of judgment, sometimes in the same room.
            </p>
            <p>
              We don’t pretend these realities don’t exist. We build for them — with safety features that take fetishization seriously, with profile prompts that invite the full conversation, and with moderation policies that protect the community from the harms members have told us they’re tired of carrying.
            </p>
          </div>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/how-to-find-a-sister-wife" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">How To Find A Sister Wife</p>
              <p className="text-sm text-slate-600 mt-1">A practical, step-by-step guide.</p>
            </a>
            <a href="/sister-wives-and-poly-family-building" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Sister Wives & Poly Family Building</p>
              <p className="text-sm text-slate-600 mt-1">Healthy structures for plural families.</p>
            </a>
            <a href="/poly-dating-for-couples" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Poly Dating For Couples</p>
              <p className="text-sm text-slate-600 mt-1">For couples exploring together.</p>
            </a>
            <a href="/african-diaspora-dating" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">African Diaspora Dating</p>
              <p className="text-sm text-slate-600 mt-1">Connections across the global Black community.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
